/**
 * First-party signup source attribution (no visible UI, no third-party calls).
 * On every page load: record first touch (never overwritten for 90 days) and last
 * touch (updated only when a visit carries a source: utm, click id or outside referrer).
 * Stored in localStorage `sl_src` + a small first-party cookie `sl_src` (90d) as backup.
 * After signup the stored touches are POSTed once to /api/signup-source.
 */
const KEY = 'sl_src';
const MAX_AGE_S = 90 * 24 * 60 * 60;
const OWN_HOST_RE = /(^|\.)sweetlisteners\.com$|\.fly\.dev$|^localhost$/;

const safeLS = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

/** In-app browser from the user agent (TikTok, Instagram, Facebook, Snapchat, Reddit) or ''. */
export const inAppFromUA = (ua = '') => {
  const s = typeof ua === 'string' ? ua : '';
  if (/musical_ly|BytedanceWebview|Bytedance|TikTok|trill_\d|ByteLocale/i.test(s)) return 'tiktok';
  if (/Instagram/i.test(s)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS|\bMessenger/i.test(s)) return 'facebook';
  if (/Snapchat/i.test(s)) return 'snapchat';
  if (/\bReddit\//i.test(s)) return 'reddit';
  return '';
};

export const parseTouch = ({ search = '', referrer = '', path = '/', now = new Date(), ua = '' }) => {
  let q;
  try {
    q = new URLSearchParams(search);
  } catch (e) {
    q = new URLSearchParams('');
  }
  const t = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(k => {
    const v = (q.get(k) || '').slice(0, 100);
    if (v) t[k] = v;
  });
  const inv = (q.get('ref') || '').slice(0, 40);
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(inv)) t.invitedBy = inv;
  const promo = (q.get('promo') || '').toLowerCase().slice(0, 20);
  if (promo === 'sweettreat' || promo === 'first10') {
    t.promo = 'sweettreat';
    const psrc = (q.get('src') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 20);
    if (psrc) t.promoSrc = psrc;
  }
  // Click ids are stored as flags only (never the id itself). gbraid / wbraid are
  // Google Ads click ids for iOS app / web-to-app traffic (sent instead of gclid).
  ['fbclid', 'ttclid', 'gclid', 'gbraid', 'wbraid'].forEach(k => {
    if (q.get(k)) t[k] = true;
  });
  // Google Ads auto-tagging also adds gad_source (ad surface) and gad_campaignid
  // (numeric campaign id). Digits only, so nothing personal can ride along.
  ['gad_source', 'gad_campaignid'].forEach(k => {
    const v = (q.get(k) || '').replace(/[^0-9]/g, '').slice(0, k === 'gad_source' ? 10 : 20);
    if (v) t[k] = v;
  });
  const srcParam = (q.get('src') || '').toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 30);
  if (srcParam) t.src = srcParam;
  const inapp = inAppFromUA(ua);
  if (inapp) t.inapp = inapp;
  if (referrer) {
    try {
      const h = new URL(referrer).hostname.toLowerCase();
      if (h && !OWN_HOST_RE.test(h)) t.ref = h;
    } catch (e) {
      // ignore
    }
  }
  const hasSource = Object.keys(t).length > 0;
  t.landing = (typeof path === 'string' ? path.split('?')[0].split('#')[0] : '/').slice(0, 150);
  t.ts = now.toISOString();
  return { touch: t, hasSource };
};

const readCookie = () => {
  try {
    const m = document.cookie.match(/(?:^|; )sl_src=([^;]*)/);
    return m ? JSON.parse(decodeURIComponent(m[1])) : null;
  } catch (e) {
    return null;
  }
};

const fresh = s =>
  s && s.first && s.first.ts && Date.now() - Date.parse(s.first.ts) < MAX_AGE_S * 1000 ? s : null;

export const readStored = () => {
  const ls = safeLS();
  let s = null;
  try {
    s = ls ? JSON.parse(ls.getItem(KEY) || 'null') : null;
  } catch (e) {
    s = null;
  }
  return fresh(s) || fresh(readCookie());
};

const write = s => {
  const ls = safeLS();
  try {
    if (ls) ls.setItem(KEY, JSON.stringify(s));
  } catch (e) {
    // ignore
  }
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${KEY}=${encodeURIComponent(JSON.stringify(s))}; Max-Age=${MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
  } catch (e) {
    // ignore
  }
};

export const mergeTouch = (stored, touch, hasSource) => {
  if (!stored) return { first: touch, last: touch };
  if (hasSource) return { ...stored, last: touch };
  return stored;
};

export const captureSignupSource = () => {
  if (typeof window === 'undefined') return null;
  try {
    const { touch, hasSource } = parseTouch({
      search: window.location.search,
      referrer: document.referrer,
      path: window.location.pathname,
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
    const stored = readStored();
    const next = mergeTouch(stored, touch, hasSource);
    if (next !== stored) write(next);
    return next;
  } catch (e) {
    return null;
  }
};

/** Fire-and-forget after a successful signup. Never throws. */
export const sendSignupSource = () => {
  try {
    const s = readStored() || captureSignupSource();
    if (!s) return Promise.resolve(null);
    const { saveSignupSource } = require('./api');
    return saveSignupSource({ first: s.first, last: s.last }).catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
};
